import { faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import type { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrderCustomer, OrderLine } from '../../models/Order';
import { ShippingDataModal } from '../modals/ShippingDataModal';

interface ShippingInfoProps {
  customer?: OrderCustomer;
  orderId?: number;
  lines?: OrderLine[];
  statusId?: number;
  onUpdate?: () => void;
}

export const ShippingInfo: FC<ShippingInfoProps> = ({ customer, orderId, lines = [], statusId, onUpdate }) => {
  const { t } = useTranslation('viewOrdersModule');
  const modalRef = useRef<ModalCenterRef>(null);

  const handleOpenModal = () => {
    modalRef.current?.openModal();
  };

  // Estados donde no se debe mostrar el botón de editar
  const nonEditableStatuses = [4, 5, 6, 7]; // En preparación, Preparado, Devuelto, Enviado
  const canEdit = !statusId || !nonEditableStatuses.includes(statusId);

  return (
    <>
      <SectionBox className="bg-card-bg">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold">{t('sections.shipping-info.title')}</h2>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={handleOpenModal} className="bg-card-bg">
              <FontAwesomeIcon icon={faPen} className="!w-3 !h-3" />
            </Button>
          )}
        </div>
        <div className="pt-6 flex flex-col gap-6">
          <div>
            <h3 className="font-semibold mb-3">{t('sections.shipping-info.customer')}</h3>
            <div className="flex flex-col gap-3 text-sm">
              <span className="line-clamp-1">{customer?.name}</span>
              <span className="line-clamp-1">{customer?.phone}</span>
              <span className="line-clamp-1">{customer?.email}</span>
            </div>
          </div>
          <div className="border-t border-card-border" />
          <div>
            <h3 className="font-semibold mb-3">{t('sections.shipping-info.address')}</h3>
            <div className="flex flex-col gap-3 text-sm">
              <span className="line-clamp-1">{customer?.address.address1}</span>
              {customer?.address.address2 && <span className="line-clamp-1">{customer.address.address2}</span>}
              <span className="line-clamp-1">
                {customer?.address.zip}, {customer?.address.city}
              </span>
              <span className="line-clamp-1">
                {customer?.address.province}, {customer?.address.country_code}
              </span>
            </div>
          </div>
        </div>
      </SectionBox>

      <ShippingDataModal ref={modalRef} customer={customer} orderId={orderId} lines={lines} onUpdate={onUpdate} />
    </>
  );
};
